import { Link } from "react-router-dom";
import { LifeBuoy, EnvelopeSimple, Phone, WhatsappLogo, Book, FileText, ArrowRight, Clock, ShieldCheck } from "@phosphor-icons/react";
import { Section, PageHero, EyebrowLabel, SectionHeading, FeatureCard, CalloutCard, BulletList } from "@/components/marketing/MarketingUI";
import useSEO from "@/lib/useSEO";

const HELP_TOPICS = [
  {
    icon: FileText,
    title: "Getting started",
    description: "Creating your first project, uploading drillhole CSVs and building an initial block model.",
  },
  {
    icon: Book,
    title: "Estimation methods",
    description: "IDW, Nearest Neighbour and Ordinary Kriging — when to use which, and how to configure them.",
  },
  {
    icon: ShieldCheck,
    title: "Standards & CPR",
    description: "Mapping SRES outputs onto JORC, NI 43-101 and SAMREC reporting requirements.",
  },
  {
    icon: Clock,
    title: "Automation & jobs",
    description: "Running long-running kriging or pit optimisation jobs and downloading the results.",
  },
];

const CHANNELS = [
  {
    icon: EnvelopeSimple,
    label: "Email",
    value: "info@sresources.co",
    href: "mailto:info@sresources.co",
    note: "Response within one business day. Attach a screenshot if reporting a defect.",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+91 80799 89999",
    href: "tel:+918079989999",
    note: "Mon–Sat · 09:30 – 19:00 IST. Best for urgent enterprise incidents.",
  },
  {
    icon: WhatsappLogo,
    label: "WhatsApp",
    value: "+91 99388 72207",
    href: "https://wa.me/919938872207",
    note: "Quick clarifications, screen-shot exchanges, and onboarding help.",
  },
];

export default function SupportPage() {
  useSEO({
    title: "Support · Real geologists on every ticket",
    description: "Enterprise-grade SRES support — email, phone, WhatsApp — with named incident owners and clear response SLAs.",
    keywords: "SRES support, technical support, SLA, help centre",
    path: "/support",
  });
  return (
    <div data-testid="support-page">
      <PageHero
        eyebrow="Support"
        title="Real geologists on the other end of every ticket."
        description="Whether you are stuck on a kriging parameter, chasing an audit-quality CPR, or reporting a defect — you will speak with someone who actually uses SRES on live projects."
        testid="support-hero"
      />

      <Section className="py-20 md:py-24" testid="support-channels">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {CHANNELS.map((c, i) => (
            <a
              key={c.label}
              href={c.href}
              target={c.href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="p-7 rounded-2xl border border-slate-200 bg-white hover:border-brand-blue/50 hover:shadow-[0_12px_40px_-14px_rgba(30,111,255,0.35)] transition-all block"
              data-testid={`support-channel-${i}`}
            >
              <div className="w-11 h-11 rounded-lg bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                <c.icon size={20} weight="bold" />
              </div>
              <div className="mt-5 text-[11px] uppercase tracking-[0.22em] text-slate-500">{c.label}</div>
              <div className="mt-1 font-heading font-bold text-slate-900 text-xl">{c.value}</div>
              <p className="mt-3 text-slate-600 text-[13px] leading-relaxed">{c.note}</p>
            </a>
          ))}
        </div>
      </Section>

      <Section className="py-20 md:py-24 bg-[#F7F8FB]" testid="support-help-center">
        <SectionHeading
          eyebrow="Help centre"
          title="Popular topics"
          description="The fastest path is usually the one you can walk yourself. Full documentation is inside the workspace."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {HELP_TOPICS.map((t, i) => (
            <FeatureCard key={t.title} {...t} testid={`support-topic-${i}`} />
          ))}
        </div>
      </Section>

      <Section className="py-20 md:py-24" testid="support-sla">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.4fr] gap-12">
          <div>
            <EyebrowLabel className="mb-4">Service level</EyebrowLabel>
            <h2 className="font-heading font-black text-slate-900 text-3xl md:text-4xl tracking-tight">
              Enterprise-grade response, without the ticket-shuffle.
            </h2>
          </div>
          <div className="space-y-6">
            <SLA row="Sev 1 · Production down" value="≤ 2 hours" description="Named incident owner assigned. Hourly updates until resolved." />
            <SLA row="Sev 2 · Major feature blocked" value="≤ 1 business day" description="Root-cause hypothesis and mitigation within 24 hours." />
            <SLA row="Sev 3 · Guidance / how-to" value="≤ 2 business days" description="Written answer with a screenshot or short recording where useful." />
            <SLA row="Sev 4 · Feature request" value="Weekly triage" description="Reviewed with product every Monday, prioritised transparently." />
          </div>
        </div>
      </Section>

      <Section className="py-16 md:py-24" testid="support-cta">
        <CalloutCard
          eyebrow="Still need help?"
          title="Open a support conversation and we will follow up on the same inbox."
          description="Share your project name, browser, and a short description — we will route it to the right specialist."
          cta="Contact support"
          to="/contact"
          testid="support-cta-callout"
        />
      </Section>
    </div>
  );
}

function SLA({ row, value, description }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px,140px,1fr] gap-4 md:gap-8 items-start p-4 border-b border-slate-100">
      <div className="font-heading font-bold text-slate-900 text-[15px]">{row}</div>
      <div className="text-brand-blue font-mono font-semibold">{value}</div>
      <div className="text-slate-600 text-[14px]">{description}</div>
    </div>
  );
}
